import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { JmapAuthError, JmapClient, bodyOf, type Email, type Mailbox } from "./lib/jmap";
import { refresh, signOut, type Session } from "./lib/auth";
import { useHotkeys } from "./lib/hotkeys";
import { Icon } from "./components/Icon";
import { Compose, type Draft } from "./components/Compose";
import { MessageList, groupIntoThreads, type Conversation } from "./components/MessageList";
import { quoteForForward, quoteForReply } from "./lib/quote";
import { Reader } from "./components/Reader";
import { Shortcuts } from "./components/Shortcuts";
import { Toast, type ToastState } from "./components/Toast";
import { CalendarClient } from "./lib/calendar";
import { CalendarView } from "./components/Calendar";
import { People } from "./components/People";
import { Settings } from "./components/Settings";
import { watchSystemTheme } from "./lib/theme";
import { useSettings } from "./lib/settings";
import type { Identity } from "./lib/jmap";


type View = "mail" | "calendar" | "people";

/** Folder icons, mirroring the design's folder list. */
const ROLE_ICON: Record<string, string> = {
  inbox: "inbox",
  archive: "archive",
  drafts: "file-pen-line",
  sent: "send",
  junk: "shield-alert",
  trash: "trash-2",
};

function folderIcon(mb: Mailbox): string {
  return ROLE_ICON[mb.role ?? ""] ?? "folder";
}

/** Back to the "Name <a@b.c>" form the composer's recipient fields parse. */
function formatAddress(a: { name: string | null; email: string }): string {
  return a.name ? `${a.name} <${a.email}>` : a.email;
}

/** Long enough that a fast typist issues one query, not eight. */
const SEARCH_DEBOUNCE_MS = 250;

/** How often to ask the server whether anything changed. */
const POLL_INTERVAL_MS = 30_000;

export function MailApp({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  // The client renews its own token on a 401. Without this, the access token
  // simply expires after an hour and the app stops working mid-session.
  const client = useMemo(
    () =>
      new JmapClient(session.token, async () => {
        const next = await refresh(session);
        return next.token;
      }),
    [session],
  );
  // Shares the mail client's session and account resolution rather than opening
  // a second one — calendars and contacts live on the same JMAP account.
  const calendarClient = useMemo(() => new CalendarClient(client), [client]);

  const [view, setView] = useState<View>("mail");

  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [current, setCurrent] = useState<Mailbox | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  /** The open conversation, newest first, with bodies. */
  const [opened, setOpened] = useState<Email[]>([]);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [cursor, setCursor] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState<Draft | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [prefs, updatePref] = useSettings();

  // "System" keeps following the OS while it is selected.
  useEffect(() => watchSystemTheme(() => prefs.theme), [prefs.theme]);

  /**
   * The list shows conversations, not messages — the same reply arriving five
   * times as five rows is the thing that makes a flat list unusable once real
   * mail starts flowing.
   */
  const conversations = useMemo(
    // With conversation view off, each message is its own row — the same shape,
    // so nothing downstream has to know which mode it is in.
    () => (prefs.conversationView
      ? groupIntoThreads(emails)
      : emails.map((e) => ({
          head: e, messages: [e],
          unread: !e.keywords["$seen"],
          flagged: !!e.keywords["$flagged"],
          hasAttachment: e.hasAttachment,
        }))),
    [emails, prefs.conversationView],
  );

  const loadIdentity = useCallback(() => {
    client.identityFor(session.email).then(setIdentity).catch(() => setIdentity(null));
  }, [client, session.email]);

  useEffect(loadIdentity, [loadIdentity]);

  const searchBox = useRef<HTMLInputElement>(null);
  /** Pending mark-as-read, cancelled when another message opens first. */
  const markReadTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(markReadTimer.current), []);
  const initials = session.email.slice(0, 2).toUpperCase();

  /**
   * Single place every failure goes.
   *
   * An unrecoverable session ends the session rather than showing a message
   * about it: once renewal has failed there is nothing on this screen the
   * person can usefully do except sign in again, so take them there.
   */
  const report = useCallback((err: unknown, fallback: string) => {
    if (err instanceof JmapAuthError) {
      signOut();
      onSignOut();
      return;
    }
    setError(err instanceof Error ? err.message : fallback);
  }, [onSignOut]);

  // Typing filters as you go, but only one request per pause.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const refreshFolders = useCallback(async () => {
    const list = await client.mailboxes();
    setMailboxes(list);
    return list;
  }, [client]);

  // Load folders once, then select the inbox.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const list = await client.mailboxes();
        if (!live) return;
        setMailboxes(list);
        setCurrent(list.find((m) => m.role === "inbox") ?? list[0] ?? null);
      } catch (err) {
        if (live) report(err, "Could not load mailboxes");
      }
    })();
    return () => {
      live = false;
    };
  }, [client]);

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    setError(null);
    try {
      setEmails(await client.emails(current.id, 50, query));
    } catch (err) {
      report(err, "Could not load messages");
    } finally {
      setLoading(false);
    }
  }, [client, current, query]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * New mail has to appear on its own — a client where messages arrive only
   * when you press a button is not one you can leave open all day.
   *
   * Polls Mailbox/get and diffs the per-folder message and unread counts.
   *
   * The obvious implementation is to poll a JMAP state string, and it does not
   * work here: measured against this server, delivering a message left BOTH the
   * session `state` and the Mailbox/get `state` unchanged while the unread
   * count went 1 -> 2. Those states track the mailbox objects, not their
   * contents, so a state-watching poll would have looked correct and silently
   * never fired. The counts are the thing that actually moves.
   *
   * Mailbox/get is one request and returns the counts the folder list already
   * displays, so the tick pays for itself; the message list only reloads when a
   * count really changed. Paused while the tab is hidden and resumed on focus,
   * so a backgrounded tab is not quietly hammering the server, and suppressed
   * while composing so a reload cannot disturb a message in progress.
   */
  useEffect(() => {
    if (composing) return;
    let previous: string | null = null;
    let stopped = false;

    const fingerprint = (list: Mailbox[]) =>
      list.map((m) => `${m.id}:${m.totalEmails}:${m.unreadEmails}`).sort().join("|");

    async function tick() {
      if (stopped || document.hidden) return;
      try {
        const list = await client.mailboxes();
        if (stopped) return;
        const now = fingerprint(list);
        setMailboxes(list);
        if (previous !== null && previous !== now) await load();
        previous = now;
      } catch {
        // A failed poll is not worth an error banner; the next tick retries.
      }
    }

    void tick();
    const timer = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) void tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [client, load, composing]);

  // Changing folder or query invalidates both the selection and the cursor;
  // leaving them would act on ids that are no longer on screen.
  useEffect(() => {
    setChecked(new Set());
    setCursor(0);
  }, [current, query]);

  function goToFolder(role: string) {
    const target = mailboxes.find((m) => m.role === role);
    if (target) {
      setView("mail");
      setCurrent(target);
      setOpened([]);
    }
  }

  async function open(conversation: Conversation) {
    try {
      const head = conversation.head;

      // A draft opens back into the composer. Showing it in the reading pane
      // would be a dead end: the one thing you want to do with an unfinished
      // message is finish it.
      if (head.keywords["$draft"]) {
        const full = await client.email(head.id);
        const body = bodyOf(full);
        setComposing({
          draftId: full.id,
          to: (full.to ?? []).map(formatAddress).join(", "),
          cc: (full.cc ?? []).map(formatAddress).join(", "),
          subject: full.subject ?? "",
          // A draft composed with formatting is resumed as HTML; falling back
          // to the text part would silently flatten it on every reopen.
          body: body.text ?? "",
          html: body.html,
          inReplyTo: full.inReplyTo ?? null,
          references: full.references ?? null,
          attachments: (full.attachments ?? []).map((a) => ({
            blobId: a.blobId,
            type: a.type,
            name: a.name ?? "attachment",
            size: a.size,
          })),
        });
        return;
      }

      // Every message in the thread, bodies included, in one request.
      const ids = conversation.messages.map((m) => m.id);
      const full = await client.emailsWithBodies(ids);
      // Preserve the newest-first order; Email/get does not promise to.
      const byId = new Map(full.map((m) => [m.id, m]));
      setOpened(conversation.messages.map((m) => byId.get(m.id) ?? m));
      setCursor(conversations.findIndex((c) => c.head.id === head.id));

      // Opening a conversation reads all of it, which is what marking the head
      // alone would fail to do — the unread badge would stay lit on a thread
      // you just read to the bottom.
      const unread = conversation.messages.filter((m) => !m.keywords["$seen"]).map((m) => m.id);
      if (unread.length && prefs.markRead !== "manual") {
        // "After a pause" is Outlook's guard against marking things read while
        // arrowing past them: the timer is cancelled if another message opens
        // first, so only what was actually dwelt on gets marked.
        const delay = prefs.markRead === "delay" ? prefs.markReadSeconds * 1000 : 0;
        window.clearTimeout(markReadTimer.current);
        markReadTimer.current = window.setTimeout(() => {
          void client.setKeywords(unread, "$seen", true)
            .then(() => {
              unread.forEach((id) => markLocally(id, "$seen", true));
              void refreshFolders();
            })
            .catch(() => { /* the badge simply stays lit; nothing is lost */ });
        }, delay);
      }
    } catch (err) {
      report(err, "Could not open message");
    }
  }

  function markLocally(id: string, keyword: string, on: boolean) {
    setEmails((prev) =>
      prev.map((e) => (e.id === id ? { ...e, keywords: { ...e.keywords, [keyword]: on } } : e)),
    );
  }

  /**
   * Resolves a well-known folder, creating Archive on demand: Stalwart ships
   * Inbox/Drafts/Sent/Junk/Trash but not Archive, and a missing role would
   * otherwise make the archive action quietly do nothing.
   */
  async function folderFor(role: string): Promise<Mailbox> {
    const existing = mailboxes.find((m) => m.role === role);
    if (existing) return existing;
    if (role !== "archive") throw new Error(`No ${role} folder`);
    const id = await client.createMailbox("Archive", "archive");
    const list = await refreshFolders();
    return (
      list.find((m) => m.id === id) ?? {
        id, name: "Archive", role: "archive", parentId: null,
        totalEmails: 0, unreadEmails: 0, sortOrder: 0,
      }
    );
  }

  /** Moves messages and offers to put them back. */
  async function moveTo(ids: string[], role: string, verb: string) {
    if (ids.length === 0) return;
    const from = current;
    try {
      const target = await folderFor(role);
      await client.move(ids, target.id);
      setEmails((prev) => prev.filter((e) => !ids.includes(e.id)));
      setChecked(new Set());
      if (opened.some((m) => ids.includes(m.id))) {
        // Outlook's "After moving or deleting an item": go back to the list, or
        // keep going through the inbox without a detour through it.
        const next = prefs.afterMove === "next"
          ? conversations.find((c) => !ids.includes(c.head.id))
          : undefined;
        if (next) void open(next);
        else setOpened([]);
      }
      void refreshFolders();
      setToast({
        message: `${ids.length === 1 ? "Message" : `${ids.length} messages`} ${verb}.`,
        undo: from
          ? async () => {
              await client.move(ids, from.id);
              void load();
              void refreshFolders();
            }
          : undefined,
      });
    } catch (err) {
      report(err, "Could not move the message");
    }
  }

  /** Stars or unstars a whole conversation — the unit the list shows. */
  async function toggleFlag(conversation: Conversation) {
    const on = !conversation.flagged;
    const ids = conversation.messages.map((m) => m.id);
    ids.forEach((id) => markLocally(id, "$flagged", on));
    try {
      await client.setKeywords(ids, "$flagged", on);
    } catch {
      ids.forEach((id) => markLocally(id, "$flagged", !on)); // put the star back
      setError("Could not update the star");
    }
  }

  async function markRead(ids: string[], read: boolean) {
    if (ids.length === 0) return;
    try {
      await client.setKeywords(ids, "$seen", read);
      ids.forEach((id) => markLocally(id, "$seen", read));
      setChecked(new Set());
      if (!read && opened.some((m) => ids.includes(m.id))) setOpened([]);
      void refreshFolders();
    } catch (err) {
      report(err, "Could not update the message");
    }
  }

  /**
   * Replies to a message, fetching it in full first if we only have the list
   * version.
   *
   * Rows in the list carry a truncated `preview`, not the body, so quoting
   * straight from one produces a reply that quotes half a sentence. The reader
   * already holds the full copy; anything else is fetched.
   */
  async function replyToConversation(c: Conversation, mode: "reply" | "replyAll" | "forward") {
    const fromReader = opened.find((m) => m.id === c.head.id);
    try {
      replyTo(fromReader ?? (await client.email(c.head.id)), mode);
    } catch (err) {
      report(err, "Could not open that message to reply");
    }
  }

  /**
   * Holds a message for the undo-send window, then delivers it.
   *
   * The toast is the undo affordance, so the wait has to outlive the composer
   * that started it — hence living here rather than in Compose. Nothing is sent
   * until the timer elapses, so undo is a genuine cancellation rather than a
   * recall: the message never left.
   */
  const holdSend = useCallback((deliver: () => Promise<void>, cancel: () => void) => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      deliver()
        .then(() => { void load(); void refreshFolders(); })
        .catch((err: unknown) => {
          // The draft was deliberately left in place, so nothing is lost — say
          // so, because a failure after the composer closed is otherwise
          // invisible.
          setToast({ message: err instanceof Error ? `Not sent: ${err.message}` : "Could not send. The draft is in Drafts." });
          void load();
          void refreshFolders();
        });
    }, prefs.undoSendSeconds * 1000);

    setToast({
      message: "Sending…",
      undo: () => {
        cancelled = true;
        window.clearTimeout(timer);
        cancel();
        // Reopening the draft is the point of undo — the person wants to change
        // something, not merely to have stopped it.
        setToast({ message: "Send cancelled. The message is in Drafts." });
        void load();
        void refreshFolders();
      },
    });
  }, [prefs.undoSendSeconds, load, refreshFolders]);

  /**
   * Signs out, emptying Deleted Items first if that is switched on.
   *
   * Outlook's "Empty Deleted Items folder on sign out". Deliberately awaited
   * rather than fired and forgotten: the point is that the mail is gone before
   * the session ends, and a request abandoned mid-flight by the sign-out would
   * leave it there.
   */
  async function signOutNow() {
    if (prefs.emptyTrashOnSignOut) {
      try {
        const trash = mailboxes.find((m) => m.role === "trash");
        if (trash) {
          const ids = (await client.emails(trash.id, 500)).map((e) => e.id);
          if (ids.length) await client.destroy(ids);
        }
      } catch {
        // Not a reason to trap someone in a session they asked to leave.
      }
    }
    signOut();
    onSignOut();
  }

  function replyTo(e: Email, mode: "reply" | "replyAll" | "forward") {
    const me = session.email.toLowerCase();
    const author = e.from?.[0]?.email ?? "";
    // Reply-all keeps everyone on To/Cc except this mailbox, and never
    // duplicates the author.
    const others = [...(e.to ?? []), ...(e.cc ?? [])]
      .map((a) => a.email)
      .filter((a) => a.toLowerCase() !== me && a.toLowerCase() !== author.toLowerCase());
    const subject = e.subject ?? "";
    const isForward = mode === "forward";
    // Quote the original as HTML when it is HTML. Flattening it to text threw
    // away tables, links, images and emphasis, and handed the recipient back a
    // mangled transcript of their own message.
    const quoted = isForward ? quoteForForward(e) : quoteForReply(e);
    setComposing({
      to: isForward ? "" : author,
      cc: mode === "replyAll" ? others.join(", ") : "",
      subject: isForward
        ? (/^fwd:/i.test(subject) ? subject : `Fwd: ${subject}`)
        : (/^re:/i.test(subject) ? subject : `Re: ${subject}`),
      body: quoted.text,
      html: quoted.html,
      // Threading headers, so replies group in the recipient's client instead of
      // arriving as a new conversation. Forwards start a fresh thread and
      // deliberately carry neither.
      inReplyTo: isForward ? null : e.messageId ?? null,
      references: isForward ? null : [...(e.references ?? []), ...(e.messageId ?? [])],
    });
  }

  /**
   * The conversation a bare keystroke acts on: the cursor row.
   *
   * Actions apply to every message in it — archiving a thread should not leave
   * its older replies behind in the inbox.
   */
  const focused: Conversation | null = conversations[cursor] ?? null;
  const openedIds = opened.map((m) => m.id);
  const actionTargets =
    checked.size > 0
      ? conversations.filter((c) => checked.has(c.head.id)).flatMap((c) => c.messages.map((m) => m.id))
      : (focused?.messages.map((m) => m.id) ?? []);

  function moveCursor(delta: number) {
    if (conversations.length === 0) return;
    const next = Math.min(Math.max(cursor + delta, 0), conversations.length - 1);
    setCursor(next);
    // Only follow the cursor into the reading pane if something is already
    // open — otherwise j/k would mark everything it passes as read.
    if (opened.length) void open(conversations[next]);
  }

  function toggleChecked(conversation: Conversation) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(conversation.head.id)) next.delete(conversation.head.id);
      else next.add(conversation.head.id);
      return next;
    });
  }

  /**
   * Message actions must not fire from Calendar or People — pressing "e" while
   * looking at the calendar would silently archive whatever the mail cursor
   * happened to be resting on. Navigation, compose and the sheet stay global.
   */
  const inMail = view === "mail";
  const onMessage = (fn: () => void) => () => { if (inMail) fn(); };

  useHotkeys({
    j: onMessage(() => moveCursor(1)),
    ArrowDown: onMessage(() => moveCursor(1)),
    k: onMessage(() => moveCursor(-1)),
    ArrowUp: onMessage(() => moveCursor(-1)),
    Enter: onMessage(() => { if (focused) void open(focused); }),
    o: onMessage(() => { if (focused) void open(focused); }),
    e: onMessage(() => void moveTo(actionTargets, "archive", "archived")),
    "#": onMessage(() => void moveTo(actionTargets, "trash", "deleted")),
    "!": onMessage(() => void moveTo(actionTargets, "junk", "reported as spam")),
    u: onMessage(() => void markRead(actionTargets, false)),
    s: onMessage(() => { if (focused) void toggleFlag(focused); }),
    x: onMessage(() => { if (focused) toggleChecked(focused); }),
    // Replying acts on the newest message in the thread, which is the one being
    // answered.
    r: onMessage(() => { if (focused) void replyToConversation(focused, prefs.replyDefault); }),
    a: onMessage(() => { if (focused) void replyToConversation(focused, "replyAll"); }),
    f: onMessage(() => { if (focused) void replyToConversation(focused, "forward"); }),
    c: () => { setView("mail"); setComposing({ to: "", subject: "", body: "" }); },
    "/": () => { setView("mail"); searchBox.current?.focus(); },
    "?": () => setShowShortcuts(true),
    "g i": () => goToFolder("inbox"),
    "g s": () => goToFolder("sent"),
    "g d": () => goToFolder("drafts"),
    "g t": () => goToFolder("trash"),
    "g c": () => setView("calendar"),
    "g p": () => setView("people"),
    Escape: () => {
      if (showSettings) setShowSettings(false);
      else if (showShortcuts) setShowShortcuts(false);
      else if (composing) setComposing(null);
      else if (opened.length) setOpened([]);
      else if (searchInput) setSearchInput("");
      else if (!inMail) setView("mail");
      else (document.activeElement as HTMLElement | null)?.blur();
    },
  });

  return (
    <div className="app" data-screen-label="Syndeo Mail app">
      <header className="top">
        <div className="brand">
          <img src={`${import.meta.env.BASE_URL}assets/syndeo-mail-mark.svg`} alt="" />
          <span className="wm">Syndeo</span>
          <span className="sub">Mail</span>
        </div>
        {/* Only in Mail: the box searches messages, so offering it over the
            calendar would promise something it does not do. */}
        <div className="searchwrap">
          {view === "mail" && (
            <div className="search">
              <Icon name="search" />
              <input
                ref={searchBox}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search mail — try from:helena, has:attachment, is:unread"
                aria-label="Search mail"
                autoComplete="off"
              />
              <kbd>/</kbd>
            </div>
          )}
        </div>
        <div className="topact">
          <button className="tbtn" title="Keyboard shortcuts (?)" onClick={() => setShowShortcuts(true)}>
            <span style={{ font: "600 13px/1 Inter" }}>?</span>
          </button>
          <button className="tbtn" title="Settings" onClick={() => setShowSettings(true)}>
            <Icon name="settings" />
          </button>
          <button className="tbtn" title="Sign out" onClick={() => void signOutNow()}>
            <Icon name="log-out" />
          </button>
          <button
            className="avatar"
            title={`${session.email} — settings`}
            onClick={() => setShowSettings(true)}
          >
            {initials}
          </button>
        </div>
      </header>

      {/* `reading` drives the narrow-viewport rule that swaps the list for the
          reading pane — mail.css scopes it to .main, not .app. */}
      <div className={`main pane-${prefs.readingPane}${opened.length ? " reading" : ""}`}>
        <nav className="rail">
          {([
            ["mail", "mail", "Mail"],
            ["calendar", "calendar-days", "Calendar"],
            ["people", "users", "People"],
          ] as [View, string, string][]).map(([id, icon, label]) => (
            <button
              key={id}
              className={`rbtn${view === id ? " on" : ""}`}
              title={label}
              aria-current={view === id}
              onClick={() => setView(id)}
            >
              <Icon name={icon} /><span>{label}</span>
            </button>
          ))}
        </nav>

        {/* Calendar and People take the list + reading columns (grid-column 3/5
            in mail.css), so the folder rail stays only for mail. */}
        {view === "calendar" && <CalendarView client={calendarClient} />}
        {view === "people" && (
          <People
            client={calendarClient}
            onCompose={(email, name) => {
              setView("mail");
              setComposing({ to: `${name} <${email}>`, subject: "", body: "" });
            }}
          />
        )}

        {view === "mail" && (
          <>
        <aside className="folders">
          <button className="compose-btn" onClick={() => setComposing({ to: "", subject: "", body: "" })}>
            <Icon name="pen-line" />Compose
          </button>
          {/* .fitem / .cnt exactly as the design's renderFolders() emits them —
              mail.css targets these names; anything else lays out inline. */}
          <div id="folderList">
            {mailboxes.map((mb) => (
              <div
                key={mb.id}
                className={`fitem${current?.id === mb.id ? " on" : ""}`}
                onClick={() => { setCurrent(mb); setOpened([]); }}
                role="button"
                tabIndex={0}
                aria-current={current?.id === mb.id}
                onKeyDown={(e) => { if (e.key === "Enter") { setCurrent(mb); setOpened([]); } }}
              >
                <Icon name={folderIcon(mb)} />
                {mb.name}
                {mb.unreadEmails > 0 && <span className="cnt">{mb.unreadEmails}</span>}
              </div>
            ))}
          </div>
        </aside>

        <MessageList
          title={current?.name ?? "Mail"}
          density={prefs.density}
          previewLines={prefs.previewLines}
          senderImages={prefs.senderImages}
          hour24={prefs.hour24}
          conversations={conversations}
          selectedId={opened[0]?.id ?? null}
          cursorId={conversations[cursor]?.head.id ?? null}
          checked={checked}
          loading={loading}
          error={error}
          searching={query.trim().length > 0}
          onOpen={open}
          onRefresh={() => { void load(); void refreshFolders(); }}
          onToggleFlag={(c) => void toggleFlag(c)}
          onToggleCheck={toggleChecked}
          onCheckAll={(on) => setChecked(on ? new Set(conversations.map((c) => c.head.id)) : new Set())}
          onBulkArchive={() => void moveTo(actionTargets, "archive", "archived")}
          onBulkDelete={() => void moveTo(actionTargets, "trash", "deleted")}
          onBulkRead={() => void markRead(actionTargets, true)}
        />

        <Reader
          client={client}
          messages={opened}
          newestOnTop={prefs.newestOnTop}
          replyDefault={prefs.replyDefault}
          hour24={prefs.hour24}
          alwaysShowImages={prefs.alwaysShowImages}
          onBack={() => setOpened([])}
          onArchive={() => void moveTo(openedIds, "archive", "archived")}
          onDelete={() => void moveTo(openedIds, "trash", "deleted")}
          onSpam={() => void moveTo(openedIds, "junk", "reported as spam")}
          onUnread={() => void markRead(openedIds, false)}
          onReply={replyTo}
        />
          </>
        )}
      </div>

      {composing && (
        <Compose
          client={client}
          from={session.email}
          initial={composing}
          onClose={() => { setComposing(null); void load(); void refreshFolders(); }}
          onDiscard={() => {
            setComposing(null);
            setToast({ message: "Draft discarded." });
            void load();
            void refreshFolders();
          }}
          onSent={() => {
            setComposing(null);
            // With undo send on, holdSend has already put up its own countdown
            // toast; a second "Message sent." would contradict it.
            if (prefs.undoSendSeconds === 0) setToast({ message: "Message sent." });
            void load();
            void refreshFolders();
          }}
          undoSendSeconds={prefs.undoSendSeconds}
          alwaysShowCc={prefs.alwaysShowCc}
          signatureOnReplies={prefs.signatureOnReplies}
          onHeld={holdSend}
        />
      )}

      {showShortcuts && <Shortcuts onClose={() => setShowShortcuts(false)} />}
      {showSettings && (
        <Settings
          client={client}
          identity={identity}
          prefs={prefs}
          update={updatePref}
          onClose={() => setShowSettings(false)}
          onIdentityChanged={loadIdentity}
        />
      )}
      {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
